
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { propertyViewsService } from '@/services/propertyViewsService';

interface DashboardMetrics {
  activeDeals: number;
  pendingDeals: number;
  closedDeals: number;
  expiredDeals: number;
  propertyViews: number;
  conversionRate: string;
}

export const useDashboardMetrics = () => {
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    activeDeals: 0,
    pendingDeals: 0,
    closedDeals: 0,
    expiredDeals: 0,
    propertyViews: 0,
    conversionRate: "0%"
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchMetrics = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setError(null);
      
      // Fetch all properties with expiration data for date-aware counting
      const { data: allProperties, error: fetchError } = await supabase
        .from('properties')
        .select('id, status, contract_expiration_date, closed_date')
        .eq('seller_id', user.id);

      if (fetchError) {
        console.error('Error fetching properties:', fetchError);
        throw new Error('Failed to fetch properties');
      }

      // Apply date-aware counting logic (consistent with usePropertyStatusCounts)
      const today = new Date().toISOString().split('T')[0];
      let activeCount = 0;
      let pendingCount = 0;
      let closedCount = 0;
      let expiredCount = 0;

      (allProperties || []).forEach(p => {
        // Closed/Sold: has closed_date
        if (p.closed_date) {
          closedCount++;
          return;
        }
        
        // Expired: contract_expiration_date on or before today - don't count as active
        if (p.contract_expiration_date && p.contract_expiration_date <= today) {
          expiredCount++;
          return;
        }
        
        // Active: not closed, not expired, status is active
        if (p.status === 'active') {
          activeCount++;
        } else if (p.status === 'pending') {
          pendingCount++;
        }
      });

      const totalDeals = activeCount + closedCount + expiredCount;
      
      // Calculate conversion rate (closed deals / total deals)
      const conversionRate = totalDeals > 0 
        ? ((closedCount / totalDeals) * 100).toFixed(1) + '%'
        : '0%';

      // Fetch improved property views data (unique views)
      const totalViews = await propertyViewsService.getUserTotalViews(user.id);

      console.log('Dashboard metrics updated:', {
        activeCount,
        pendingCount,
        closedCount,
        expiredCount,
        totalViews,
        conversionRate
      });

      setMetrics({
        activeDeals: activeCount,
        pendingDeals: pendingCount,
        closedDeals: closedCount,
        expiredDeals: expiredCount,
        propertyViews: totalViews,
        conversionRate
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast({
        title: "Error loading dashboard metrics",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, [user]);

  // Set up real-time subscription for property changes
  useEffect(() => {
    if (!user) return;

    let propertiesChannel: ReturnType<typeof supabase.channel> | null = null;
    let viewsChannel: ReturnType<typeof supabase.channel> | null = null;

    try {
      propertiesChannel = supabase
        .channel('dashboard-metrics-properties')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'properties',
            filter: `seller_id=eq.${user.id}`
          },
          () => {
            fetchMetrics();
          }
        )
        .subscribe();

      viewsChannel = supabase
        .channel('dashboard-metrics-views')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'property_views'
          },
          () => {
            fetchMetrics();
          }
        )
        .subscribe();
    } catch (error) {
      console.warn('Failed to subscribe to dashboard metrics (Safari security restriction?):', error);
    }

    return () => {
      try {
        if (propertiesChannel) supabase.removeChannel(propertiesChannel);
        if (viewsChannel) supabase.removeChannel(viewsChannel);
      } catch (error) {
        console.warn('Error cleaning up dashboard metrics subscription:', error);
      }
    };
  }, [user]);

  return { metrics, isLoading, error, refetch: fetchMetrics };
};
